/**
 * A Pokémon type as a coloured disc with a white glyph: our own icon set
 * (client/public/type-icons/*.svg, 2026-09-10). It replaced Pokémon GO's own
 * type discs, which were Scopely-owned game assets (see docs/ip-audit.md).
 * One component so every surface (species header, dex filter, meta panels,
 * the Mega bonus card) shows the same picture.
 *
 * `size` is the disc's diameter in px; the art is SVG so it stays crisp at
 * every size the app uses (14 to 40).
 */
import type { CSSProperties } from "react";
import { TYPE_INFO, typeIconSrc, type PokeType } from "@/lib/types";

export function TypeIcon({
  type,
  size = 16,
  className,
  style,
  title,
}: {
  type: PokeType;
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** Defaults to the type's label; pass "" to hide the tooltip. */
  title?: string;
}) {
  const label = TYPE_INFO[type]?.label ?? type;
  return (
    <img
      src={typeIconSrc(type)}
      alt={label}
      title={title ?? label}
      width={size}
      height={size}
      draggable={false}
      className={className}
      style={{ width: size, height: size, objectFit: "contain", flexShrink: 0, ...style }}
      data-testid={`type-icon-${type}`}
    />
  );
}
